import { fetchUtils } from "react-admin";
import { stringify } from "query-string";
import { apiUrl } from "../config";

type HttpClient = typeof fetchUtils.fetchJson;

const httpClient: HttpClient = fetchUtils.fetchJson;

function extractListResponse(json: any) {
  const data = Array.isArray(json)
    ? json
    : Array.isArray(json?.resultat)
      ? json.resultat
      : Array.isArray(json?.data)
        ? json.data
        : [];
  const total = Number(json?.total);

  return {
    data,
    total: Number.isFinite(total) ? total : data.length,
  };
}

function authHeaders() {
  const t = typeof window !== "undefined" ? localStorage.getItem("token") : null;
  const headers = new Headers();
  if (t) headers.set("Authorization", `Bearer ${t}`);
  headers.set("Content-Type", "application/json");
  return headers;
}

/** Upload multipart vers /upload -> renvoie l'URL publique du fichier stocké */
async function uploadImageAndGetUrl(file: File): Promise<string> {
  const form = new FormData();
  form.append("file", file);

  // IMPORTANT: ne pas fixer "Content-Type" manuellement avec FormData
  const tok = typeof window !== "undefined" ? localStorage.getItem("token") : null;

  const res = await fetch(`${apiUrl}/upload`, {
    method: "POST",
    headers: tok ? { Authorization: `Bearer ${tok}` } : undefined,
    body: form,
  });

  if (!res.ok) {
    const msg = await res.text().catch(() => "");
    throw new Error(`Échec de l'upload (${res.status}) ${msg}`);
  }

  const json = await res.json();
  // Adapter au retour de l’API : { url: "https://.../file.jpg" } ou { path: "/uploads/file.jpg" }
  const url = json?.url ?? json?.path;
  if (!url || typeof url !== "string") {
    throw new Error("Réponse d'upload invalide: URL manquante");
  }
  return url;
}

/** Nettoie le payload d’un record qui contient un champ image (React-Admin ImageInput) */
async function normalizeImageField(data: any) {
  const mydata = { ...data };

  // Nouveau fichier sélectionné
  if (mydata.image && mydata.image.rawFile instanceof File) {
    const url = await uploadImageAndGetUrl(mydata.image.rawFile as File);
    mydata.image = url; // on stocke l’URL
    return mydata;
  }

  // Édition sans changement d'image : on laisse string URL telle quelle
  if (typeof mydata.image === "string") return mydata;

  // Cas où RA fournit { src: "https://...", title: "..."} (édition)
  if (mydata.image && typeof mydata.image.src === "string") {
    mydata.image = mydata.image.src;
  }

  return mydata;
}

/** Selon la ressource, prépare le payload (ici: stay avec champ image) */
async function preparePayload(resource: string, data: any) {
  if (resource === "stay") {
    return normalizeImageField(data);
  }
  return data;
}

export const dataProvider = {
  /** LIST */
  getList: async (
    resource: string,
    params: {
      pagination: { page: number; perPage: number };
      sort: { field: string; order: "ASC" | "DESC" };
      filter: Record<string, any>;
    }
  ) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;

    const query = {
      sort: JSON.stringify([field, order]),
      range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
      filter: JSON.stringify(params.filter || {}),
    };

    const url = `${apiUrl}/${resource}?${stringify(query)}`;

    const { json } = await httpClient(url, { headers: authHeaders() });
    return extractListResponse(json);
  },

  /** ONE */
  getOne: async (resource: string, params: { id: string | number }) => {
    const { json } = await httpClient(`${apiUrl}/${resource}/${params.id}`, {
      headers: authHeaders(),
    });
    return { data: json };
  },

  /** MANY */
  getMany: async (resource: string, params: { ids: Array<string | number> }) => {
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    };
    const url = `${apiUrl}/${resource}?${stringify(query)}`;
    const { json } = await httpClient(url, { headers: authHeaders() });
    return { data: extractListResponse(json).data };
  },

  /** MANY REF */
  getManyReference: async (
    resource: string,
    params: {
      target: string;
      id: string | number;
      pagination: { page: number; perPage: number };
      sort: { field: string; order: "ASC" | "DESC" };
      filter: Record<string, any>;
    }
  ) => {
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;

    const query = {
      sort: JSON.stringify([field, order]),
      range: JSON.stringify([(page - 1) * perPage, page * perPage - 1]),
      filter: JSON.stringify({ ...params.filter, [params.target]: params.id }),
    };
    const url = `${apiUrl}/${resource}?${stringify(query)}`;

    const { json } = await httpClient(url, { headers: authHeaders() });
    return extractListResponse(json);
  },

  /** UPDATE */
  update: async (resource: string, params: { id: string | number; data: any }) => {
    const body = await preparePayload(resource, params.data);

    const { json } = await httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
    return { data: json };
  },

  /** UPDATE MANY */
  updateMany: async (
    resource: string,
    params: { ids: Array<string | number>; data: any }
  ) => {
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    };
    const { json } = await httpClient(`${apiUrl}/${resource}?${stringify(query)}`, {
      method: "PUT",
      headers: authHeaders(),
      body: JSON.stringify(params.data),
    });

    const ids = Array.isArray(json) ? json.map((r: any) => r.id) : params.ids;
    return { data: ids };
  },

  /** CREATE */
  create: async (resource: string, params: { data: any }) => {
    const body = await preparePayload(resource, params.data);

    const { json } = await httpClient(`${apiUrl}/${resource}`, {
      method: "POST",
      headers: authHeaders(),
      body: JSON.stringify(body),
    });
    return { data: { ...json } };
  },

  /** DELETE */
  delete: async (resource: string, params: { id: string | number }) => {
    const { json } = await httpClient(`${apiUrl}/${resource}/${params.id}`, {
      method: "DELETE",
      headers: authHeaders(),
    });
    return { data: json ?? { id: params.id } };
  },

  /** DELETE MANY */
  deleteMany: async (resource: string, params: { ids: Array<string | number> }) => {
    const query = {
      filter: JSON.stringify({ id: params.ids }),
    };
    const { json } = await httpClient(`${apiUrl}/${resource}?${stringify(query)}`, {
      method: "DELETE",
      headers: authHeaders(),
    });

    const ids = Array.isArray(json) ? json.map((r: any) => r.id) : params.ids;
    return { data: ids };
  },
};

export default dataProvider;
